import React from "react";
import BulkTicketTable from "./components/table";
import { useCopies } from "shared/hooks/useCopies";
import useBulkTicketManageService from "./service";
import AlertModal from "../../../../shared/components/alertModal";
import { AssignTicketFromMyExperience } from "./components/assignTicket";
import BuyBulkTicket from "./components/buyBulkTicket";
import { BulkTicketManagementProps, BulkTicketManageService } from "./types";
import Icon from "assets/icons";
import NavMenuPopover from "shared/components/navMenuPopover";
import { BulkTicketTransfer } from "./components/bulkTicketTransfer";
import { AppType } from "shared/utils/enums";
import Button from "shared/components/button";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import Size from "shared/enums/Size";
import { BulkTicketAssignedParticipantsTable } from "shared/components/bulkTicket/bulkTicketTable";
import { BulkTicketsPurchasedDashboard } from "shared/components/bulkTicket/bulkTicketDashboard";
import NoDataFound from "shared/components/noDataFound";
import { useLocation } from "react-router-dom";

export default function BulkTicketManagement(props: BulkTicketManagementProps) {
  const tableRef = React.useRef<any>();
  const service = useBulkTicketManageService(props);

  return (
    <React.Fragment>
      {props.isOpenInModal ? (
        <BulkTicketManagementModalFlow service={service} tableRef={tableRef} closeHandler={props.modalCloseHandler} />
      ) : (
        <BulkTicketManagementPageFlow service={service} tableRef={tableRef} />
      )}
    </React.Fragment>
  );
}

function BulkTicketManagementModalFlow({
  service,
  tableRef,
  closeHandler
}: { service: BulkTicketManageService; tableRef: React.MutableRefObject<any> } & {
  closeHandler: BulkTicketManagementProps["modalCloseHandler"];
}) {
  const copies = useCopies();
  return (
    <Modal size={Size.FULL_WIDTH} className="bulkTicketInModal">
      <ModalHeader
        left={copies.get("bulk.tickets.label")}
        right={<HeaderActions service={service} />}
        onClose={closeHandler}
      />
      <ModalBody>
        <ModalAndPageBodyFlow service={service} tableRef={tableRef} isInModal />
      </ModalBody>
    </Modal>
  );
}

function BulkTicketManagementPageFlow({
  service,
  tableRef
}: {
  service: BulkTicketManageService;
  tableRef: React.MutableRefObject<any>;
}) {
  const copies = useCopies();
  return (
    <div className="blkTicketWrapper">
      <div className="blkTicketHeader">
        <div className="blkTicketHeader_left">
          <h6 className="blkTicketHeader_leftTitle">{copies.get("bulk.tickets.label")}</h6>
        </div>
        <HeaderActions service={service} />
      </div>
      <ModalAndPageBodyFlow service={service} tableRef={tableRef} />
    </div>
  );
}

function HeaderActions({ service }: { service: BulkTicketManageService }) {
  const copies = useCopies();
  const location = useLocation();

  return (
    <div className="blkTicketHeader_rightActions">
      <div className="blkTicketHeader_rightActions--buyTicket" key={location.pathname}>
        {service.bulkTicketForms.length > 0 && (
          <NavMenuPopover
            label={copies.get("buy.bulk.ticket")}
            header={copies.get("buy.tickets.in.bulk")}
            headerClass="blkTicketHeader_rightActions--menuTitle"
            buttonIcon={<Icon.BulkTickets top={-1} />}
            idsToHighlight={[
              ...service.paymentPendingInventoryForms.invoicePending,
              ...service.paymentPendingInventoryForms.offlinePending
            ]}
            menuOptions={service.bulkTicketForms.map(item => {
              return {
                id: item.formId,
                key: item.formId,
                label: item.formName,
                clickHandler: () => service.openBuyMoreModalHandler(item.formId)
              };
            })}
          />
        )}
      </div>
      {service.hasValidBulkTicketPurchases && (
        <div className="blkTicketHeader_rightActions--manage">
          <Button
            onClick={() => service.openTransferModalHandler()}
            label={copies.get("manage")}
            isOutline
            icon={<Icon.Settings size={16} />}
          />
        </div>
      )}
    </div>
  );
}

function ModalAndPageBodyFlow({
  service,
  tableRef,
  isInModal
}: {
  service: BulkTicketManageService;
  tableRef: React.MutableRefObject<any>;
  isInModal?: boolean;
}) {
  return (
    <React.Fragment>
      <div className="blkTicketContainer">
        <BulkTicketsPurchasedDashboard bulkTicketsList={service.data} handleAssign={service.openAssignModalHandler} />
        {service.data.length > 0 ? (
          <BulkTicketAssignedParticipantsTable tableRef={tableRef} isInModal={isInModal}>
            <BulkTicketTable
              tableRef={tableRef}
              list={service.assignedTicketList}
              revokeTicketHandler={service.revokeTicketHandler}
              noRecordMessage={service.noRecordMessage}
            />
          </BulkTicketAssignedParticipantsTable>
        ) : (
          <NoDataFound infoText={service.noRecordMessage} />
        )}
      </div>

      {service.state.alertConfig && <AlertModal {...service.state.alertConfig} />}
      {service.state.openAssignTicket && (
        <AssignTicketFromMyExperience
          handleClose={service.closeAssignModalHandler}
          bulkTicketId={service.selectedBulkTicketId}
          ticketFormName={service.selectedTicketFormNameByBulkTicketId}
          ticketFormId={service.data.find(item => item.id === service.selectedBulkTicketId)?.ticketFormId || ""}
        />
      )}
      {service.state.openBuyMoreTicket && (
        <BuyBulkTicket
          refetchList={service.refetch}
          closeHandler={service.closeBuyMoreModalHandler}
          ticketFormId={service.selectedTicketFormId}
          ticketFormName={service.selectedTicketFormNameByTicketFormId}
        />
      )}
      {service.isTransferModalOpen && (
        <BulkTicketTransfer
          registrationParticipantId={service.registrationParticipantId}
          handleTransferSuccess={service.refetch}
          closeHandler={service.closeTransferModalHandler}
          bulkTickets={service.data || []}
          renderIn={AppType.MYEXPERIENCE}
        />
      )}
    </React.Fragment>
  );
}
